import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getDirectionSummary, toRoundedScore } from './chemistryResultUtils';
import './Chemistry.css';

function getDirectionResult(tx, attentionFlow, nameA, nameB) {
  const safeNameA = nameA || 'A';
  const safeNameB = nameB || 'B';
  const summary = getDirectionSummary(attentionFlow, safeNameA, safeNameB);

  if (summary.lead === 'balanced') {
    return {
      icon: '♡',
      lead: 'balanced',
      tone: 'balanced',
      title: tx('summary.balanced_title', { nameA: safeNameA, nameB: safeNameB }),
      body: tx('summary.balanced_body'),
    };
  }

  return {
    icon: '♡',
    lead: summary.lead,
    tone: summary.tone,
    title: tx('summary.lead_title', { leadName: summary.leadName, targetName: summary.targetName }),
    body: tx('summary.lead_body', { leadName: summary.leadName, targetName: summary.targetName }),
    leadName: summary.leadName,
    targetName: summary.targetName,
  };
}

function getFlowStatus(tx, score) {
  if (score >= 55) {
    return {
      label: tx('attention.status_fast'),
      body: tx('attention.description_fast'),
      emphasis: 'lead-soft',
    };
  }
  if (score <= 45) {
    return {
      label: tx('attention.status_slow'),
      body: tx('attention.description_slow'),
      emphasis: 'secondary',
    };
  }
  return {
    label: tx('attention.status_similar'),
    body: tx('attention.description_similar'),
    emphasis: 'even',
  };
}

function getRepresentativePoint(tx, appeal, salience) {
  const components = {
    ...(appeal?.components || {}),
    ...(salience?.components || {}),
  };
  const candidates = [
    ['eye_pull', tx('premium.component_eye')],
    ['eye_charm', tx('premium.component_eye')],
    ['structural_presence', tx('premium.component_dimension')],
    ['face_dimension', tx('premium.component_dimension')],
    ['harmony', tx('premium.component_balance')],
    ['averageness', tx('premium.component_balance')],
    ['uniqueness', tx('premium.component_unique')],
    ['distinctiveness', tx('premium.component_unique')],
    ['symmetry', tx('premium.component_order')],
  ];

  return candidates.reduce(
    (best, [key, label]) => {
      const value = Number(components[key] ?? -Infinity);
      return value > best.value ? { value, label } : best;
    },
    { value: -Infinity, label: tx('premium.component_eye') },
  ).label;
}

function buildPremiumQuestions(tx, chemistryScore, magnetScore, mirrorScore, directionResult) {
  return [
    chemistryScore >= 82
      ? tx('questions.high_score') : tx('questions.score_factors'),
    directionResult.lead === 'balanced'
      ? tx('questions.flow_balanced') : tx('questions.flow_difference'),
    magnetScore >= mirrorScore
      ? tx('questions.visual_tension') : tx('questions.structural_comfort'),
    directionResult.lead === 'balanced'
      ? tx('questions.visible_feature_balanced') : tx('questions.visible_feature_direction', { leadName: directionResult.leadName, targetName: directionResult.targetName }),
    mirrorScore >= 55
      ? tx('questions.stability_condition') : tx('questions.familiarity_condition'),
  ];
}

function ReportSection({ eyebrow, title, description, children }) {
  return (
    <section className="chem-report-section">
      <div className="chem-report-heading">
        <span className="chem-report-eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function PairAvatar({ src, name, accent }) {
  return (
    <div className="chem-pair-avatar">
      {src ? (
        <img src={src} alt={name} style={{ borderColor: accent }} />
      ) : (
        <div className="chem-pair-avatar-fallback" style={{ borderColor: accent, background: `${accent}22` }} />
      )}
      <span>{name}</span>
    </div>
  );
}

function ResultPhotoPanel({ src, name, label, accent }) {
  const [failedSource, setFailedSource] = useState('');
  const imageFailed = Boolean(src && failedSource === src);

  return (
    <article className="chem-result-photo-panel" style={{ '--chem-result-accent': accent }}>
      <div className="chem-result-photo-topline">
        <div>
          <span>{label}</span>
          <strong>{name}</strong>
        </div>
      </div>
      <div className="chem-result-photo-frame">
        {src && !imageFailed ? (
          <img src={src} alt={name} onError={() => setFailedSource(src)} />
        ) : (
          <div className="chem-result-photo-fallback">{name.slice(0, 1)}</div>
        )}
      </div>
    </article>
  );
}

function AttentionPortraitCard({
  image,
  name,
  score,
  description,
  accent = 'pink',
  emphasis = 'even',
}) {
  const accentColor = accent === 'pink' ? '#ff6b8a' : '#818cf8';

  return (
    <article className={`chem-attention-portrait chem-attention-portrait-${accent} is-${emphasis}`}>
      <div className="chem-attention-aura" aria-hidden="true" />
      <div className="chem-attention-photo">
        {image ? (
          <img src={image} alt={name} />
        ) : (
          <div className="chem-attention-photo-fallback" style={{ borderColor: accentColor, background: `${accentColor}22` }}>
            {name.slice(0, 1)}
          </div>
        )}
      </div>
      <strong className="chem-attention-portrait-name">{name}</strong>
      <span className="chem-attention-portrait-context">{description.context}</span>
      <strong className="chem-attention-portrait-result">{description.label}</strong>
      <p>{description.body}</p>
      <small>{description.scoreLabel} {score}</small>
    </article>
  );
}

function ReasonCard({ title, body, tone = 'pink' }) {
  return (
    <div className={`chem-reason-card chem-reason-card-${tone}`}>
      <div className="chem-reason-person">
        <div>
          <strong>{title}</strong>
        </div>
      </div>
      <p>{body}</p>
    </div>
  );
}


function getAttentionSummary(tx, attentionFlow, nameA, nameB) {
  const result = getDirectionResult(tx, attentionFlow, nameA, nameB);
  if (result.lead === 'balanced') {
    return {
      title: result.title,
      badge: tx('attention.balanced_badge'),
      summary: result.body,
      tone: 'balanced',
      level: 'balanced',
      lead: 'balanced',
    };
  }

  return {
    title: result.title,
    badge: tx('attention.lead_badge'),
    summary: result.body,
    tone: result.tone,
    level: 'soft',
    lead: result.lead,
  };
}

function getAppealLine(tx, appeal) {
  const harmony = appeal?.components?.harmony ?? 50;
  const uniqueness = appeal?.components?.uniqueness ?? 50;

  if (harmony >= 68 && uniqueness >= 62) {
    return tx('reasons.appeal_harmony_unique');
  }
  if (uniqueness >= 66) {
    return tx('reasons.appeal_unique');
  }
  if (harmony >= 66) {
    return tx('reasons.appeal_harmony');
  }
  return tx('reasons.appeal_gradual');
}

function getSalienceLine(tx, appeal, salience) {
  const eyePull = appeal?.components?.eye_pull ?? 50;
  const uniqueness = appeal?.components?.uniqueness ?? 50;
  const salienceTotal = salience?.total ?? 50;

  if (eyePull >= 68 || salienceTotal >= 65) {
    return tx('reasons.salience_strong');
  }
  if (uniqueness >= 62) {
    return tx('reasons.salience_distinct');
  }
  return tx('reasons.salience_soft');
}

function getApproachabilityLine(tx, approachability) {
  const label = approachability?.label || 'neutral';

  if (label === 'open') {
    return tx('reasons.approach_open');
  }
  if (label === 'hard_to_approach') {
    return tx('reasons.approach_reserved');
  }
  return tx('reasons.approach_neutral');
}

function getDifferenceLine(tx, attractionIntensity, perceivedComfort) {
  if (attractionIntensity >= 75 && perceivedComfort >= 65) {
    return tx('reasons.difference_both');
  }
  if (attractionIntensity >= 75) {
    return tx('reasons.difference_attraction');
  }
  if (perceivedComfort >= 68) {
    return tx('reasons.difference_comfort');
  }
  return tx('reasons.difference_gradual');
}

function buildPremiumDirectionCard(tx, directionResult, appealA, appealB, salienceA, salienceB) {
  if (directionResult.lead === 'balanced') {
    return {
      title: tx('premium.flow_balanced_title'),
      body: tx('premium.flow_balanced_body'),
      level: tx('premium.level_balanced'),
      tone: 'mint',
    };
  }

  const targetAppeal = directionResult.lead === 'a' ? appealB : appealA;
  const targetSalience = directionResult.lead === 'a' ? salienceB : salienceA;
  const point = getRepresentativePoint(tx, targetAppeal, targetSalience);
  return {
    title: tx('premium.flow_lead_title', { leadName: directionResult.leadName, targetName: directionResult.targetName }),
    body: tx('premium.flow_lead_body', { leadName: directionResult.leadName, targetName: directionResult.targetName, point }),
    level: tx('premium.level_lead'),
    tone: directionResult.lead === 'a' ? 'pink' : 'blue',
  };
}

function buildTensionCard(tx, attractionIntensity, perceivedComfort, flowDelta) {
  if (attractionIntensity >= 78 && perceivedComfort < 62) {
    return {
      title: tx('premium.tension_title'), body: tx('premium.tension_body'),
      level: tx('premium.level_pace'),
      tone: 'pink',
    };
  }

  if (perceivedComfort >= 72 && flowDelta <= 6) {
    return {
      title: tx('premium.comfort_title'), body: tx('premium.comfort_body'),
      level: tx('premium.level_comfort'),
      tone: 'mint',
    };
  }

  return {
    title: tx('premium.context_title'), body: tx('premium.context_body'),
    level: tx('premium.level_context'),
    tone: 'pink',
  };
}

function buildSceneCard(tx, attractionIntensity, perceivedComfort) {
  if (attractionIntensity >= 75 && perceivedComfort >= 68) {
    return {
      title: tx('premium.scene_title'), body: tx('premium.scene_both'),
      level: tx('premium.level_scene'),
      tone: 'mint',
    };
  }

  if (attractionIntensity >= 75) {
    return {
      title: tx('premium.scene_title'), body: tx('premium.scene_attraction'),
      level: tx('premium.level_scene'),
      tone: 'pink',
    };
  }

  return {
    title: tx('premium.scene_title'), body: tx('premium.scene_gradual'),
    level: tx('premium.level_comfort'),
    tone: 'mint',
  };
}

function buildDatingTipCard(tx, aToB, bToA) {
  const delta = Math.abs(aToB - bToA);
  if (delta <= 6) {
    return {
      title: tx('premium.tip_title'), body: tx('premium.tip_balanced'),
      level: tx('premium.level_balanced'),
      tone: 'mint',
    };
  }

  return {
    title: tx('premium.tip_title'), body: tx('premium.tip_asymmetric'),
    level: tx('premium.level_pace'),
    tone: 'pink',
  };
}

function VisualAttractionReport({
  nameA,
  nameB,
  imageA,
  imageB,
  rankTitle,
  chemistryScore,
  gradeCode,
  gradeLabel,
  rarityLabel,
  scoreInterpretation,
  attractionIntensity,
  perceivedComfort,
  attentionFlow,
  attractionFlow,
  directionality,
  salienceA,
  salienceB,
  appealA,
  appealB,
  approachabilityA,
  approachabilityB,
  onPremiumClick,
  isPremiumUnlocked = false,
  isPremiumLoading = false,
  onSave,
  onShare,
  onCopyLink,
  isSaving = false,
  isSharing = false,
}) {
  const { t } = useTranslation();
  const tx = useCallback((key, options) => t(`chemistry_result.report.${key}`, options), [t]);
  const safeNameA = nameA || 'A';
  const safeNameB = nameB || 'B';
  const safeAttentionFlow = useMemo(
    () => attractionFlow || attentionFlow || directionality || { a_to_b: 50, b_to_a: 50, delta: 0, label: 'balanced' },
    [attractionFlow, attentionFlow, directionality],
  );
  const safeAttractionFlow = safeAttentionFlow;
  const safeSalienceA = useMemo(() => salienceA || { total: 50, components: {} }, [salienceA]);
  const safeSalienceB = useMemo(() => salienceB || { total: 50, components: {} }, [salienceB]);
  const safeAppealA = useMemo(() => appealA || { total: 50, components: {} }, [appealA]);
  const safeAppealB = useMemo(() => appealB || { total: 50, components: {} }, [appealB]);
  const safeApproachabilityA = approachabilityA || { total: 50, label: 'neutral' };
  const safeApproachabilityB = approachabilityB || { total: 50, label: 'neutral' };

  const aToBAttraction = toRoundedScore(safeAttractionFlow.a_to_b);
  const bToAAttraction = toRoundedScore(safeAttractionFlow.b_to_a);
  const attractionDelta = Math.abs(aToBAttraction - bToAAttraction);
  const isALead = aToBAttraction >= bToAAttraction;
  const leadApproachability = isALead ? safeApproachabilityB : safeApproachabilityA;
  const leadAppeal = isALead ? safeAppealB : safeAppealA;
  const leadSalience = isALead ? safeSalienceB : safeSalienceA;
  const chemistryBalance = toRoundedScore(chemistryScore, Math.round((Number(attractionIntensity || 0) + Number(perceivedComfort || 0)) / 2));

  const directionResult = useMemo(
    () => getDirectionResult(tx, safeAttentionFlow, safeNameA, safeNameB),
    [safeAttentionFlow, safeNameA, safeNameB, tx],
  );
  const attentionSummary = useMemo(
    () => getAttentionSummary(tx, safeAttentionFlow, safeNameA, safeNameB),
    [safeAttentionFlow, safeNameA, safeNameB, tx],
  );

  const reasonCards = useMemo(
    () => [
      {
        title: tx('reasons.appeal_title'), body: getAppealLine(tx, leadAppeal),
        tone: 'pink',
      },
      {
        title: tx('reasons.salience_title'), body: getSalienceLine(tx, leadAppeal, leadSalience),
        tone: 'blue',
      },
      {
        title: tx('reasons.approach_title'), body: getApproachabilityLine(tx, leadApproachability),
        tone: 'mint',
      },
      {
        title: tx('reasons.difference_title'), body: getDifferenceLine(tx, attractionIntensity, perceivedComfort),
        tone: 'pink',
      },
    ],
    [leadAppeal, leadApproachability, leadSalience, attractionIntensity, perceivedComfort, tx],
  );

  const premiumDirection = useMemo(
    () => buildPremiumDirectionCard(tx, directionResult, safeAppealA, safeAppealB, safeSalienceA, safeSalienceB),
    [directionResult, safeAppealA, safeAppealB, safeSalienceA, safeSalienceB, tx],
  );
  const premiumTension = useMemo(
    () => buildTensionCard(tx, attractionIntensity, perceivedComfort, attractionDelta),
    [attractionIntensity, perceivedComfort, attractionDelta, tx],
  );
  const premiumScene = useMemo(
    () => buildSceneCard(tx, attractionIntensity, perceivedComfort),
    [attractionIntensity, perceivedComfort, tx],
  );
  const premiumDatingTip = useMemo(
    () => buildDatingTipCard(tx, aToBAttraction, bToAAttraction),
    [aToBAttraction, bToAAttraction, tx],
  );

  const premiumQuestions = useMemo(
    () => buildPremiumQuestions(tx, chemistryBalance, attractionIntensity, perceivedComfort, directionResult),
    [chemistryBalance, attractionIntensity, perceivedComfort, directionResult, tx],
  );

  const attentionAToB = toRoundedScore(safeAttentionFlow.a_to_b);
  const attentionBToA = toRoundedScore(safeAttentionFlow.b_to_a);
  const attentionAState = getFlowStatus(tx, attentionAToB);
  const attentionBState = getFlowStatus(tx, attentionBToA);

  return (
    <div className="chem-report-shell">
      <section className="chem-report-hero">
        <div className="chem-result-photo-stage">
          <ResultPhotoPanel src={imageA} name={safeNameA} label={tx('hero.person_a')} accent="#ff6b8a" />
          <div className="chem-result-photo-connector" aria-hidden="true">
            <span />
            <strong>♡</strong>
            <span />
          </div>
          <ResultPhotoPanel src={imageB} name={safeNameB} label={tx('hero.person_b')} accent="#818cf8" />
        </div>

        <div className="chem-report-copy">
          <span className="chem-report-kicker">{tx('hero.kicker')}</span>
          <strong className="chem-summary-relation-type">{rankTitle}</strong>
          <div className={`chem-result-summary-card chem-result-summary-card-${directionResult.tone}`}>
            <span className="chem-result-summary-icon" aria-hidden="true">{directionResult.icon}</span>
            <div>
              <strong>{directionResult.title}</strong>
              <p>{directionResult.body}</p>
            </div>
          </div>
          <div className="chem-hero-score-row">
            <div className="chem-summary-score-block">
              <strong>{chemistryBalance}</strong>
              <span>{tx('hero.out_of_100')}</span>
            </div>
            <div className="chem-hero-score-copy">
              <div className="chem-summary-meta">
                <span className="chem-grade-code">{gradeCode}</span>
                <span>{gradeLabel}</span>
              </div>
              <span className="chem-rarity-badge">{rarityLabel}</span>
              <p className="chem-score-interpretation">{scoreInterpretation}</p>
            </div>
          </div>
        </div>
      </section>

      <details className="chem-detail-accordion chem-detail-accordion-primary" open>
        <summary>
          <span>{tx('detail.label')}</span>
          <strong>{tx('detail.toggle')}</strong>
        </summary>
        <div className="chem-detail-accordion-body">
          <ReportSection
            eyebrow={tx('attention.eyebrow')}
            title={tx('attention.section_title')}
            description={tx('attention.section_description')}
          >
            <div className={`chem-attention-conclusion chem-attention-conclusion-${attentionSummary.tone}`}>
              <span className="chem-attention-conclusion-badge">{attentionSummary.badge}</span>
              <strong>{attentionSummary.title}</strong>
              <p>{attentionSummary.summary}</p>
            </div>
            <div className="chem-attention-story">
              <AttentionPortraitCard
                image={imageA}
                name={safeNameA}
                score={attentionAToB}
                description={{ context: tx('attention.context', { name: safeNameB }), scoreLabel: tx('attention.score_label'), label: attentionAState.label, body: attentionAState.body }}
                accent="pink"
                emphasis={attentionAState.emphasis}
              />
              <AttentionPortraitCard
                image={imageB}
                name={safeNameB}
                score={attentionBToA}
                description={{ context: tx('attention.context', { name: safeNameA }), scoreLabel: tx('attention.score_label'), label: attentionBState.label, body: attentionBState.body }}
                accent="blue"
                emphasis={attentionBState.emphasis}
              />
            </div>
          </ReportSection>
        </div>
      </details>

      <section className="chem-report-section chem-share-preview-section">
        <div className="chem-report-heading">
          <span className="chem-report-eyebrow">{tx('share.eyebrow')}</span>
          <h2>{tx('share.title')}</h2>
          <p>{tx(chemistryBalance >= 75 ? 'share.high_description' : 'share.description')}</p>
        </div>
        <div className="chem-share-link-panel">
          <div className="chem-share-link-copy">
            <strong>{directionResult.title}</strong>
            <p>{tx('share.link_description', { defaultValue: tx(chemistryBalance >= 75 ? 'share.high_description' : 'share.description') })}</p>
          </div>
          <div className="chem-share-action-grid">
            <button type="button" className="funnel-cta-primary chem-share-primary" onClick={onShare} disabled={isSharing || isSaving}>
              {isSharing ? tx('share.preparing') : tx('share.button')}
            </button>
            <button type="button" className="funnel-cta-secondary chem-share-action" onClick={onSave} disabled={isSaving || isSharing}>
              {isSaving ? tx('share.saving') : tx('share.save')}
            </button>
            <button type="button" className="funnel-cta-secondary chem-copy-action" onClick={onCopyLink}>
              {tx('share.copy')}
            </button>
          </div>
        </div>
      </section>

      <details className="chem-detail-accordion">
        <summary>
          <span>{tx('detail.more_label', { defaultValue: tx('detail.label') })}</span>
          <strong>{tx('detail.more_toggle', { defaultValue: tx('detail.toggle') })}</strong>
        </summary>
        <div className="chem-detail-accordion-body">
          <ReportSection
            eyebrow={tx('detail.eyebrow')}
            title={tx('detail.title')}
            description={tx('detail.description')}
          >
        <div className="chem-reason-grid">
          {reasonCards.map((card) => (
            <ReasonCard key={card.title} title={card.title} body={card.body} tone={card.tone} />
          ))}
        </div>
      </ReportSection>


          {isPremiumUnlocked ? (
        <ReportSection
          eyebrow={tx('premium.eyebrow')}
          title={tx('premium.title')}
          description={tx('premium.description')}
        >
          <div className="chem-deep-grid">
            {[premiumDirection, premiumTension, premiumScene, premiumDatingTip].map((card) => (
              <div key={card.title} className="chem-deep-card">
                <div className="chem-deep-card-top">
                  <strong>{card.title}</strong>
                  <span className={`chem-level-pill chem-level-pill-${card.tone}`}>{card.level}</span>
                </div>
                <p>{card.body}</p>
              </div>
            ))}
          </div>
        </ReportSection>
      ) : isPremiumLoading ? (
        <section className="chem-report-section chem-premium-lock-card">
          <div className="chem-premium-lock-header">
            <span className="chem-premium-badge">Premium</span>
            <h2>{tx('premium.loading_title')}</h2>
            <p>{tx('premium.loading_description')}</p>
          </div>
        </section>
      ) : (
        <section className="chem-report-section chem-premium-lock-card">
          <div className="chem-premium-lock-header">
            <span className="chem-premium-badge">Premium</span>
            <h2>{tx('premium.locked_title')}</h2>
            <p>{tx('premium.locked_description')}</p>
          </div>

          <div className="chem-premium-lock-preview" aria-hidden="true">
            <div className="chem-premium-lock-blur" />
            <div className="chem-premium-lock-glow" />
          </div>

          <ul className="chem-premium-lock-list">
            {premiumQuestions.map((item) => (
              <li key={item}><span className="chem-lock-icon" aria-hidden="true">LOCK</span>{item}</li>
            ))}
          </ul>

          <div className="chem-premium-lock-actions">
            <button type="button" onClick={onPremiumClick} className="funnel-cta-primary chem-premium-cta">
              {tx('premium.unlock')}
            </button>
            <p>{tx('premium.payment_hint')}</p>
          </div>
        </section>
          )}
        </div>
      </details>

    </div>
  );
}


ReportSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
};

PairAvatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  accent: PropTypes.string,
};

ResultPhotoPanel.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  accent: PropTypes.string,
};

AttentionPortraitCard.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  score: PropTypes.number,
  description: PropTypes.shape({
    context: PropTypes.string,
    body: PropTypes.string,
    scoreLabel: PropTypes.string,
    label: PropTypes.string,
  }),
  accent: PropTypes.string,
  emphasis: PropTypes.string,
};

ReasonCard.propTypes = {
  title: PropTypes.string,
  body: PropTypes.string,
  tone: PropTypes.string,
};

VisualAttractionReport.propTypes = {
  nameA: PropTypes.string,
  nameB: PropTypes.string,
  imageA: PropTypes.string,
  imageB: PropTypes.string,
  rankTitle: PropTypes.string,
  chemistryScore: PropTypes.number,
  gradeCode: PropTypes.string,
  gradeLabel: PropTypes.string,
  rarityLabel: PropTypes.string,
  scoreInterpretation: PropTypes.string,
  attractionIntensity: PropTypes.number,
  perceivedComfort: PropTypes.number,
  attentionFlow: PropTypes.object,
  attractionFlow: PropTypes.object,
  directionality: PropTypes.object,
  salienceA: PropTypes.object,
  salienceB: PropTypes.object,
  appealA: PropTypes.object,
  appealB: PropTypes.object,
  approachabilityA: PropTypes.object,
  approachabilityB: PropTypes.object,
  onPremiumClick: PropTypes.func,
  isPremiumUnlocked: PropTypes.bool,
  isPremiumLoading: PropTypes.bool,
  onSave: PropTypes.func,
  onShare: PropTypes.func,
  onCopyLink: PropTypes.func,
  isSaving: PropTypes.bool,
  isSharing: PropTypes.bool,
};

export default VisualAttractionReport;
